import { ConnectorService } from './../_services/http-api/connector.service';
import { RoutePage } from './../app-routes.enum';
import { uniqBy } from 'lodash';
import { Observable, Subject as RxjsSubject, Subscription, zip } from 'rxjs';
import { auditTime, catchError, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  InspectionReport,
  InspectionReportListItem,
  InspectionReportMetaData,
  ReportDetail
} from '../_objects/inspection-report';
import { environment } from "../../environments/environment";
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationError } from '../_errors/application-error';
import { SortFilter } from '../_objects/filter';
import { InspectionDetailUpdate, InspectionForSharedLock, InspectionList, InspectionListItem } from '../_objects/inspection';
import { Subject } from '../_objects/subject';
import { SubjectModelListItem } from '../_objects/subject-model';
import { TenantUser } from '../_objects/user';
import { InspectionSchema } from '../_offline/models/inspection.schema';
import { UtilsService } from '../_services/_utils.service';
import { CommonSnackBarService } from '../_services/common-snack-bar.service';
import { InspectionDbService } from '../_services/database/inspection-db.service';
import { InspectionImageService } from '../_services/http-api/inspection-image.service';
import { InspectionReportService } from '../_services/http-api/inspection-report.service';
import { InspectionService } from '../_services/http-api/inspection.service';
import { SubjectModelService } from '../_services/http-api/subject-model.service';
import { SubjectService } from '../_services/http-api/subject.service';
import { TenantUserService } from '../_services/http-api/tenant-user.service';
import { LangUpdateService } from '../_services/lang-update.service';
import { NetworkStatusService } from '../_services/network-status.service';
import { OfflineDataSyncService } from '../_services/offline-data-sync.service';
import { SpinnerService } from '../_services/spinner.service';
import { INSPECTON_LIST___QUERY_PARAMS, QueryParam, StorageService, SubjectFilter } from '../_services/storage.service';
import { FilterData } from '../_shared/dialog/filter-select-dialog/filter-select-dialog.component';
import {
  FILTER_CONTENT_ID,
  INSPECTION_REPORT_STATUS,
  INSPECTION_STATUS,
  MODEL_CODE,
  PAGINATION_VALUES,
  PERMISSION_CODE,
  SCREEN_SIZE
} from '../app.constants';
import { CommonDeleteDialogComponent } from '../common-dialog/common-delete-dialog.component';
import { CommonLayoutService } from '../common-layout/common-layout.service';
import {
  InspectionUpsertDialogComponent
} from '../inspection/dialogs/inspection-upsert-dialog/inspection-upsert-dialog.component';
import { SharedLockService } from "../_services/shared-lock/shared-lock.service";
import { SendOfflineDataService } from '../_services/sync/send-offline-data.service';
import { IconEnum } from '@evc/web-components';
import { CallToActionLabel } from '../shared/directives/cta/cta.directive';
import { OnPremiseFirstSyncService } from '../_services/sync/sync-on-premise-data/on-premise-first-sync.service';
import { ConnectorStatus } from '../_objects/connector';

export class ReportHeader {
  id = '';
  name = 'ReportHeader';
  inspectorName = '';
}

type InspectionListSort = {
  value: null | 'inspectionDate' | 'title',
  direction: null | 'asc' | 'desc'
};

export class InspectionItemWrapper extends InspectionListItem {
  inspectorId: string = '';

  constructor(item: InspectionListItem, inspectorId: string) {
    super();
    this.id = item.id;
    this.inspectionDate = item.inspectionDate;
    this.inspectors = item.inspectors;
    this.permission = item.permission;
    this.status = item.status;
    this.subjectModelId = item.subjectModelId;
    this.subjectModelName = item.subjectModelName;
    this.subjectSerialNo = item.subjectSerialNo;
    this.title = item.title;
    this.sharedLockBy = item.sharedLockBy;
    this.isOfflineData = item.isOfflineData;
    this.inspectorId = inspectorId;
    this.imageCount = item.imageCount;
    this.connectorTenantId = item.connectorTenantId;
    this.reportCount = item.reportCount;
  }
}

export class InspectionReportListItemWrapper extends InspectionReportListItem {
  id = '';
  name = '';
  inspectorName = '';
}

@Component({
    selector: 'app-inspection-list',
    templateUrl: './inspection-list.component.html',
    styleUrls: ['./inspection-list.component.scss'],
    animations: [
        trigger('reportExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class InspectionListComponent implements OnInit, OnDestroy {
  callToActionLabel: CallToActionLabel = {
    label: this.translate.instant('commonHeadnavi.newInspection'),
    icon: IconEnum.Plus
  };
  readonly modelCode = MODEL_CODE.IPLEXNX;

  filterData: FilterData[] = [];
  sortData: InspectionListSort = { value: 'inspectionDate', direction: 'desc' };

  public imageCountPerInspection: { [inspectionId: string]: number } = {};
  public reportsMetaDataPerInspection: { [inspectionId: string]: InspectionReportMetaData } = {};

  // Statusフィルタコンボ用
  inspectionStatus = INSPECTION_STATUS;
  reportStatus: Map<string, string> = new Map<string, string>(INSPECTION_REPORT_STATUS);

  // Subjectフィルタコンボ用
  subjectModels: SubjectModelListItem[];

  isOwn = false;

  inspectionDataSource: MatTableDataSource<InspectionItemWrapper | InspectionReportListItemWrapper | ReportHeader>
    = new MatTableDataSource<InspectionItemWrapper | InspectionReportListItemWrapper | ReportHeader>();

  // S/Nフィルタコンボ用
  subjects: Subject[]; // 全候補

  // ソートボタン用

  inspectionList: InspectionList;
  tenantUsers: TenantUser[] = [];
  tenantUserIds: string[] = [];

  connectors = signal<Record<string, ConnectorStatus>>({});

  isNarrow: boolean;

  onDestroy$ = new RxjsSubject();
  onWindowResize$ = new RxjsSubject();
  onRefresh$ = new RxjsSubject();

  expanded: InspectionListItem;

  sortFilters: SortFilter[];
  currentNetworkMode: boolean;

  subList: Subscription[] = [];

  inspSchema: InspectionSchema[];
  isRecordsLoading = true;
  pageIndex = PAGINATION_VALUES.START_INDEX;
  pageSize = PAGINATION_VALUES.DEFAULT_PAGE_SIZE;

  currentlyHoveredInspection = "";
  isCompactScreen = false;
  hoveredSortType: string = null;
  activeSort: string = 'inspectionDate';

  private switchOnline: boolean = false;

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;

  constructor(
    private router: Router,
    private inspectionService: InspectionService,
    private inspectionReportService: InspectionReportService,
    private subjectModelService: SubjectModelService,
    private subjectService: SubjectService,
    private tenantUserService: TenantUserService,
    private utilsService: UtilsService,
    private storageService: StorageService,
    private dialog: MatDialog,
    private snackBar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private cd: ChangeDetectorRef,
    private networkStatusService: NetworkStatusService,
    private breakpointObserver: BreakpointObserver,
    private offlineDataSyncService: OfflineDataSyncService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private inspectionDbService: InspectionDbService,
    private commonLayoutService: CommonLayoutService,
    private inspectionImageService: InspectionImageService,
    private sharedLockService: SharedLockService,
    private syncData: SendOfflineDataService,
    private firstSyncService: OnPremiseFirstSyncService,
    private connectorService: ConnectorService
  ) {
    this.langService.prepare(translate, this.onDestroy$, () => {
      if (this.sortFilters) {
        this.sortFilters.filter(value => value.column === 'inspectionDate').forEach(value => value.label = this.translate.instant('inspectionlist.inspectionDate'));
        this.sortFilters.filter(value => value.column === 'title').forEach(value => value.label = this.translate.instant('inspectionlist.title'));
      }
    });
    const navigation = this.router.getCurrentNavigation();

    this.switchOnline = navigation?.extras?.state?.['switchOnline'] || false;

    this.breakpointObserver.observe(['(max-width: 920px)'])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result => {
        this.isNarrow = result.matches;
      });
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isDesktopAndOnline() {
    return this.networkStatusService.isDesktop() && this.networkStatusService.isOnline;
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get inspectionFound(): boolean {
    return (this.inspectionList?.items?.length ?? 0) > 0;
  }

  public onFilterChange(filterData: FilterData[]) {
    this.filterData = filterData;
    this.initInspectionList();
  }

  public onSortChange(sortField: 'inspectionDate' | 'title'): void {
    this.activeSort = sortField;
    if (this.sortData.value === sortField) {
      this.sortData.direction = this.sortData.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortData = {
        value: sortField,
        direction: 'asc'
      };
    }
    this.initInspectionList();
  }

  ngOnInit() {
    this.computeWindowSize();

    this.setInspectionListSubscribe();

    if (this.networkStatusService.isDesktop()) {
      this.currentNetworkMode = this.networkStatusService.isOnline;
      this.getInspectionSchema();
      this.router.events
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(event => {
          if (event instanceof NavigationEnd) {
            if (this.currentNetworkMode) {
              this.inspectionList = new InspectionList();
              this.inspectionDataSource =
                new MatTableDataSource<InspectionItemWrapper | InspectionReportListItemWrapper | ReportHeader>();
            }
            this.desktopSetupAsync();
            this.getInitData();
          }
        });
    }

    if (!this.isOffline) {
      this.checkConnectorsStatus();
    }

    this.initializeInvites();

    this.firstSyncService.onPremiseFirstSyncCompleted$.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.initInspectionList());
  }

  ngOnDestroy() {
    this.subList.forEach(_ => _.unsubscribe());
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.onRefresh$.next(undefined);
    this.onRefresh$.unsubscribe();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.onWindowResize$.next(event);
  }

  // HeaderにあるNew Inspectionの押下を監視
  setInspectionListSubscribe() {
    this.commonLayoutService
      .commonLayoutAddInspectionObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.addInspection());

    this.onWindowResize$
      .pipe(auditTime(500))
      .pipe(tap(_ => this.computeWindowSize()))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => this.onLoadNext());
  }

  async initializeInvites() {
    if (this.networkStatusService.isDesktop() && this.offlineDataSyncService.isFirst) {
      await this.desktopSetupAsync();
      this.getInitData();
    } else {
      this.getInitData();
    }
  }

  async desktopSetupAsync() {
    this.syncData.syncData()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (_syncResult) => {
        this.networkStatusService.switchOnlineMode();
        await this.offlineDataSyncService.deleteUploadedOfflineData();
        this.initInspectionList();
      });
  }

  getInitData() {
    this.initInspectionList();

    zip(
      // Obtain the information necessary to initialize the screen from the service
      this.subjectModelService.getSubjectModels(),
      this.subjectService.getSubjects(),
    )
    .pipe(takeUntil(this.onDestroy$))
    .subscribe({
      next: values => {
        this.subjectModels = values[0];
        this.subjects = values[1];
      },
      error: error => {
        console.log(error);
        this.snackBar.open(this.translate.instant('inspectionlist.ngOnInitFailed'));
      }
    });
  }

  initInspectionList() {
    this.inspectionList = new InspectionList();
    this.inspectionList.total = 0;
    this.inspectionList.page = 1;
    this.inspectionList.items = [];
    this.oscSpinnerService.show();
    this.updateInspectionList(true);
  }

  updateInspectionList(isRefresh = false): void {
    this.isRecordsLoading = true;
    if (isRefresh) {
      this.onRefresh$.next(undefined);
      this.pageIndex = PAGINATION_VALUES.START_INDEX;
      this.pageSize = PAGINATION_VALUES.DEFAULT_PAGE_SIZE;
    }

    const findFilter = (id): FilterData | undefined => this.filterData.find(i => i.id == id);
    const subjectFilter = new SubjectFilter(
      findFilter(FILTER_CONTENT_ID.SUBJECT_MODEL)?.value as string,
      findFilter(FILTER_CONTENT_ID.SUBJECT)?.value as string,
      findFilter(FILTER_CONTENT_ID.OWNER)?.value as string,
      findFilter(FILTER_CONTENT_ID.COUNTRY)?.value as string,
      findFilter(FILTER_CONTENT_ID.STATE)?.value as string,
      findFilter(FILTER_CONTENT_ID.DISTRICT)?.value as string,
      findFilter(FILTER_CONTENT_ID.SITE)?.value as string,
      findFilter(FILTER_CONTENT_ID.PROJECT)?.value as string);

    const queryParam = this.genQueryParams(
      findFilter(FILTER_CONTENT_ID.STATUS)?.value as string,
      subjectFilter,
      findFilter(FILTER_CONTENT_ID.IS_OWN)?.value as boolean);

    this.updateInspectionListSub(queryParam)
      .pipe(takeUntil(this.onDestroy$))
      .pipe(takeUntil(this.onRefresh$))
      .subscribe({
        next: response => {
          this.inspectionList.total = response.total;
          this.inspectionList.page = response.page;
          this.inspectionList.items = isRefresh
            ? response.items
            : uniqBy([...this.inspectionList.items, ...response.items], 'id');
          this.resetScrollBarPosition();
        },
        error: error => {
          console.log(error);
          this.expanded = null;
          this.oscSpinnerService.hide();
          this.isRecordsLoading = false;
        },
        complete: () => {
          if (!this.switchOnline)
            this.oscSpinnerService.hide();

          this.isRecordsLoading = false;
          this.handleInspectionList(this.inspectionList);
          this.expanded = null;
        }
      });
  }

  /**
   * Resetting the scroll bar position avoids continuous load when the scroll bar is at the bottom.
   * @private
   */
  private resetScrollBarPosition() {
    if (this.scrollContainer == null) {
      return;
    }
    const scrollContainer = this.scrollContainer.nativeElement;
    const offsetHeight = scrollContainer.offsetHeight;
    const buffer = offsetHeight * 0.1; // 10% of the visible height
    scrollContainer.scrollTop = scrollContainer.scrollTop - buffer;
  }

  updateInspectionListSub(param: QueryParam): Observable<InspectionList> {
    // Save search conditions to internal storage
    this.storageService.pushQueryParam(INSPECTON_LIST___QUERY_PARAMS, param);

    return this.inspectionService.getInspections(
      param.filter.status, param.filter.subjectFilter, param.filter.isOwn,
      { active: param.sort.active, direction: param.sort.direction },
      { index: param.page.index + 1, size: param.page.size });
  }

  // Update total and page index
  handleInspectionList(inspList: InspectionList) {
    if (!inspList || (!inspList.total && inspList.total !== 0) || inspList.total < 0 ||
      !inspList.page || inspList.page < 1 || !inspList.items) {
      throw new ApplicationError('InspectionList is invalid.');
    }

    this.loadInspectionItemWrapper(inspList.items);

    this.getTenantUsers();
    this.displayReportAndImageIndicator();
  }

  getTenantUsers() {
    this.tenantUserService.getUsers().subscribe((users)=>{
      this.tenantUsers = users;
      this.tenantUserIds = this.tenantUsers.filter(user => user.id).map(user => user.id);
    });
  }

  loadInspectionItemWrapper(items: InspectionListItem[]) {
    const inspectionWrapperList = items.map(item => {
      return new InspectionItemWrapper(item, item.inspectors[0].id);
    });

    this.setTableData(inspectionWrapperList);
    this.cd.detectChanges();
  }

  gotoDataManagementAsNew(inspection: InspectionListItem): void {
    if (!inspection || !inspection.id) {
      throw new ApplicationError('Inspection is invalid.');
    }

    if (this.isInspectionDisabled(inspection)) {
      return;
    }

    this.router.navigate([RoutePage.InspectionDataManagement, 'inspection', inspection.id]);
  }

  isInspectionDisabled(inspection: InspectionListItem): boolean {
    return inspection.sharedLockBy && (!this.isOffline || !this.isLockedByCurrentUser(inspection));
  }

  onClickReportRow(report: ReportDetail, inspection: InspectionListItem) {
    this.gotoInspectionReport(report, inspection);
  }

  gotoDataManagementAsEdit(report: InspectionReport, inspection: InspectionListItem): void {
    let param: NavigationExtras;
    if (this.networkStatusService.isDesktop() && this.networkStatusService.isOnline && this.isLockedByCurrentUser(inspection)) {
      param = { queryParams: { offlineDataUpdate: true } };
    }
    this.router.navigate([RoutePage.InspectionDataManagement, 'report', report.reportId], param);
  }

  gotoInspectionReport(report: ReportDetail, inspection: InspectionListItem): void {
    let param: NavigationExtras;
    if (this.networkStatusService.isDesktop() && this.networkStatusService.isOnline && this.isLockedByCurrentUser(inspection)) {
      param = { queryParams: { offlineDataUpdate: true } };
    }

    this.router.navigate([RoutePage.InspectionReport, report.reportId], param);
  }

  deleteInspection(inspection: InspectionListItem): void {
    if (!inspection) {
      throw new ApplicationError('Inspection is invalid.');
    }

    const title = [];
    title.push(inspection.title);

    this.openDeleteDialog(title).subscribe(result => {
      if (result) {
        this.deleteInspectionSub(inspection.id);
      }
    });
  }

  openDeleteDialog(title: string[]): Observable<any> {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: (this.translate.instant('inspectionlist.inspection')), deleteNames: title }
    });
    return dialogRef.afterClosed();
  }

  deleteInspectionSub(id: string): void {
    this.oscSpinnerService.show();

    this.inspectionService.deleteInspection(id).subscribe(
      _ => {
        this.inspectionList.items = this.inspectionList.items.filter(item => item.id !== id);
        this.inspectionList.total = this.inspectionList.total - 1;
        this.handleInspectionList(this.inspectionList);
        this.oscSpinnerService.hide();
      },
      error => {
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant('inspectionlist.deleteInspectionSubFailed'));
        console.log(error);
      }
    );
  }

  isReadOnly(inspection: InspectionListItem): boolean {

    return !inspection.permission || (inspection.permission & PERMISSION_CODE.WRITE) === 0;
  }

  genQueryParams(status: string, subjectFilter: SubjectFilter, isOwn: boolean): QueryParam {
    return {
      filter: { status, subjectFilter, isOwn, labels: this.filterData },
      sort: { active: this.sortData.value, direction: this.sortData.direction },
      page: { index: this.pageIndex, size: this.pageSize },
    };
  }

  setTableData(data: (InspectionItemWrapper | InspectionReportListItemWrapper | ReportHeader)[]): void {
    this.inspectionDataSource =
      new MatTableDataSource<InspectionItemWrapper | InspectionReportListItemWrapper | ReportHeader>(data);
  }

  addInspection() {
    const dialogRef = this.openAddInspectionDialog();
    // 検査追加後
    dialogRef.componentInstance
      .inserted
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()), take(1))
      .subscribe(inspection => {
        dialogRef.close();
        this.router.navigate([RoutePage.InspectionDataManagement, 'inspection', inspection.id]);
      });

  }

  openAddInspectionDialog(): MatDialogRef<InspectionUpsertDialogComponent> {
    return this.dialog.open(InspectionUpsertDialogComponent, {
      minWidth: '680px',
      panelClass: 'dialog-scroll',
      data: {
        subjects: this.subjects,
        subjectModels: this.subjectModels,
      }
    });
  }

  isLockedByCurrentUser(inspection: InspectionListItem) {
    return this.sharedLockService.isLockedByCurrentUser(inspection);
  }

  getInspectionSchema() {
    this.inspectionDbService.getListAll()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(insp => {
        this.inspSchema = insp;
      });
  }

  toggleInspectionCheckout(inspectionId: string) {
    this.oscSpinnerService.show();
    this.inspectionService.getInspection(inspectionId).subscribe(inspection => {
      const params = new InspectionForSharedLock(inspection.id);
      this.inspectionService.updateInspectionSharedLock(params).subscribe({
        next: inspection => {
          this.inspectionList.items.some(item => {
            if (item.id === inspection.id) {
              item.sharedLockBy = inspection.sharedLockBy;
              return true;
            }
          });
          this.handleInspectionList(this.inspectionList);
          this.oscSpinnerService.hide();
        },
        error: error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('inspectionList.toggleCheckoutError'));
          this.oscSpinnerService.hide();
        }
      });
    });
  }

  isCheckoutInspection(inspection: InspectionItemWrapper | InspectionListItem) {
    return this.sharedLockService.isInspectionLocked(inspection);
  }

  isDisableReportButton(inspection: InspectionItemWrapper): boolean {
    const report = this.reportsMetaDataPerInspection[inspection.id];

    if (!report) {
      return true;
    }

    return this.isOffline ? false : this.isLockedByCurrentUser(inspection);
  }

  /**
   * Inspectorを取得済みならTrue
   */
  isDspInspector(inspectorId: string): boolean {
    return this.tenantUserIds.includes(inspectorId);
  }

  onLoadNext() {
    if (!this.isRecordsLoading && this.inspectionList?.items.length < this.inspectionList.total) {
      this.isRecordsLoading = true;
      this.pageIndex++;
      this.updateInspectionList();
    }
  }

  getDynamicScrollDistance(): number {
    const itemsLength = this.inspectionList?.items.length || 0;
    if (itemsLength < 100) {
      return 6;
    } else if (itemsLength < 500) {
      return 3;
    } else {
      return 2;
    }
  }

  hoverIn(inspection: InspectionItemWrapper) {
    if (!this.isDisableReportButton(inspection))
      this.currentlyHoveredInspection = inspection.id;
  }

  hoverOut() {
    this.currentlyHoveredInspection = "";
  }

  isHover(inspectionId: string): boolean {
    return this.currentlyHoveredInspection === inspectionId;
  }

  getStatusHoverMessage(inspection: InspectionItemWrapper) {
    if (this.isCheckoutInspection(inspection)) {
      return this.getLockedStatusMessage(inspection);
    }

    return this.translate.instant('inspectionlist.' + this.inspectionStatus.get(inspection.status).toLowerCase());
  }

  getLockedStatusMessage(inspection: InspectionItemWrapper) {
    const user = this.tenantUsers.find(user =>
      user.id?.toLowerCase() === inspection.sharedLockBy?.toLowerCase()
    );

    return user
      ? this.translate.instant('inspectionreportlist.checkedOutBy') + user.givenName + ' ' + user.surname
      : this.translate.instant('inspectionreportlist.checkedOut');
  }

  toggleInspectionSharing(inspection: InspectionItemWrapper, tenantId: ConnectorStatus["connectorId"]) {
    const connectorTenantIdUpdate = inspection.connectorTenantId === tenantId ? null : tenantId;

    this.oscSpinnerService.show();

    this.inspectionService.getInspection(inspection.id)
      .pipe(
        map(inspection => {
          const inspectionUpdate: InspectionDetailUpdate = {
            id: inspection.id,
            isValid: inspection.isValid,
            locationVersion: inspection.locationVersion,
            title: inspection.title,
            subjectId: inspection.subjectId,
            inspectionDate: inspection.inspectionDate,
            inspectors: inspection.inspectors,
            status: inspection.status,
            schemaVersion: inspection.schemaVersion,
            imageCount: inspection.imageCount,
            connectorTenantId: connectorTenantIdUpdate,
            reportCount: inspection.reportCount
          };

          return inspectionUpdate;
        }),
        switchMap(inspectionUpdate =>
          this.inspectionService.updateInspection(inspectionUpdate)
        ),
        tap(updatedInspection => {
          const inspectionListUpdate = this.inspectionList.items.map(entry => {
            if (entry.id === inspection.id) {
              entry.connectorTenantId = connectorTenantIdUpdate;
            }
            return entry;
          });

          this.handleInspectionList(this.inspectionList);

          this.snackBar.open(
            this.translate.instant('inspectionSharing.toggleSharingSuccess')
          );
        }),
        catchError(error => {
          console.error(error);

          this.snackBar.open(
            this.translate.instant('inspectionSharings.toggleSharingError')
          );

          return "";
        }),
        finalize(() => {
          this.oscSpinnerService.hide();
        })
    ).subscribe();
  }

  private computeWindowSize() {
    this.isCompactScreen = this.utilsService.getScreenSize() === SCREEN_SIZE.COMPACT;
  }

  private displayReportAndImageIndicator() {
    if (this.isOffline) {
      this.getInspectionImageCount();
      this.getInspectionReportMetaData();
    }
  }

  private getInspectionReportMetaData() {
    const inspectionIds = this.inspectionDataSource.data.map(i => i.id);
    const missingIds = inspectionIds.filter(id => !Object.keys(this.reportsMetaDataPerInspection).includes(id));

    this.inspectionReportService.getReportsMetaDataForInspections(missingIds)
      .pipe(takeUntil(this.onDestroy$))
      .pipe(takeUntil(this.onRefresh$))
      .subscribe({
        next: reportsPerInspection => {
          reportsPerInspection.forEach((item) => {
            this.reportsMetaDataPerInspection[item.inspectionId] = item;
          });
        },
        error: error => {
          console.error(error);
        }
      });

  }

  private getInspectionImageCount() {
    const inspectionIds = this.inspectionDataSource.data.map(i => i.id);
    const missingIds = inspectionIds.filter(id => !Object.keys(this.imageCountPerInspection).includes(id));
    this.inspectionImageService.getImageCountForInspections(missingIds)
      .pipe(takeUntil(this.onDestroy$))
      .pipe(takeUntil(this.onRefresh$))
      .subscribe({
        next: inspectionImagesCount => {
          inspectionImagesCount.forEach((item) => {
            this.imageCountPerInspection[item.inspectionId] = item.count;
          });
        },
        error: error => {
          console.error(error);
        }
      });
  }

  private updateConnectorStatus(connectorName: string, connectorStatus: ConnectorStatus) {
    this.connectors.update(current => ({
      ...current,
      [connectorName]: connectorStatus
    }));
  }

  private checkConnectorsStatus() {
    Object
      .entries(environment.CONNECTORS)
      .forEach(([connectorName, connectorId]: [string, string]) => {
        this.connectorService.getConnectorStatus(connectorId).subscribe({
          next: (connectorStatus: ConnectorStatus) => {
            this.updateConnectorStatus(connectorName, connectorStatus);
          },
          error: (error) => {
            console.warn(error);
            this.updateConnectorStatus(connectorName, {
              connectorId,
              connectorName,
              isEnabled: false
            });
          }
        });
      });
  }
}
